import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Helper to get storage based on remember preference
const getStorage = () => {
  // Check if user chose to be remembered (stored in localStorage)
  const rememberMe = localStorage.getItem('remember_me') === 'true';
  return rememberMe ? localStorage : sessionStorage;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);
  const [impersonatedChurch, setImpersonatedChurch] = useState(null);
  const [originalToken, setOriginalToken] = useState(null);
  const [originalUser, setOriginalUser] = useState(null);

  // Backend URL - Use environment variable in production
  const API = process.env.REACT_APP_BACKEND_URL 
    || (window.location.origin.includes('localhost')
      ? 'http://localhost:8001'
      : window.location.origin);

  // Clear all auth data (must be defined before useEffect)
  const clearAuth = () => {
    setToken(null);
    setUser(null);
    setImpersonating(false);
    setImpersonatedChurch(null);
    setOriginalToken(null);
    setOriginalUser(null);
    
    // Clear from both storages to be safe
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    localStorage.removeItem('remember_me');
    localStorage.removeItem('impersonating');
    localStorage.removeItem('impersonated_church');
    localStorage.removeItem('original_token');
    localStorage.removeItem('original_user');
    
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('auth_user');
    sessionStorage.removeItem('impersonating');
    sessionStorage.removeItem('impersonated_church');
    sessionStorage.removeItem('original_token');
    sessionStorage.removeItem('original_user');
  };

  // Initialize auth from storage
  useEffect(() => {
    // Try localStorage first (for remembered users), then sessionStorage
    let storedToken = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    let storedUser = localStorage.getItem('auth_user') || sessionStorage.getItem('auth_user');
    let storedImpersonating = localStorage.getItem('impersonating') || sessionStorage.getItem('impersonating');
    let storedImpersonatedChurch = localStorage.getItem('impersonated_church') || sessionStorage.getItem('impersonated_church');
    let storedOriginalToken = localStorage.getItem('original_token') || sessionStorage.getItem('original_token');
    let storedOriginalUser = localStorage.getItem('original_user') || sessionStorage.getItem('original_user');

    if (storedToken && storedUser) {
      // Check if token has church_id (required for new auth system)
      try {
        const tokenPayload = JSON.parse(atob(storedToken.split('.')[1]));
        if (!tokenPayload.church_id && tokenPayload.role !== 'bishop' && tokenPayload.role !== 'super_admin') {
          // Old token without church_id - clear and force re-login
          console.log('Old token detected, clearing auth...');
          clearAuth();
          setLoading(false);
          return;
        }
      } catch (e) {
        // Invalid token format - clear auth
        clearAuth();
        setLoading(false);
        return;
      }
      
      setToken(storedToken);
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        clearAuth();
        setLoading(false);
        return;
      }
      
      if (storedImpersonating === 'true') {
        setImpersonating(true);
        try {
          setImpersonatedChurch(storedImpersonatedChurch ? JSON.parse(storedImpersonatedChurch) : null);
          setOriginalUser(storedOriginalUser ? JSON.parse(storedOriginalUser) : null);
        } catch (e) {
          // Ignore parse errors
        }
        setOriginalToken(storedOriginalToken);
      }
      
      // Verify token is still valid
      axios.get(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      }).then(response => {
        setUser(response.data);
        // Update storage with fresh user data
        const storage = getStorage();
        storage.setItem('auth_user', JSON.stringify(response.data));
      }).catch(() => {
        // Token invalid, clear auth
        clearAuth();
      });
    }
    setLoading(false);
  }, [API]);

  const login = async (username, password, rememberMe = true) => {
    try {
      // Try multi-church login first (supports bishop and church users)
      let response;
      try {
        response = await axios.post(`${API}/api/auth/login/multi`, {
          username,
          password,
          remember_me: rememberMe
        });
      } catch (multiError) {
        // Fallback to regular login if multi endpoint not available
        response = await axios.post(`${API}/api/auth/login`, {
          username,
          password,
          remember_me: rememberMe
        });
      }

      const { access_token, user: userData } = response.data;

      setToken(access_token);
      setUser(userData);

      // Always use localStorage to remember the user (default behavior)
      // Store remember_me preference
      localStorage.setItem('remember_me', 'true');
      localStorage.setItem('auth_token', access_token);
      localStorage.setItem('auth_user', JSON.stringify(userData));
      
      // Also keep in sessionStorage for backward compatibility
      sessionStorage.setItem('auth_token', access_token);
      sessionStorage.setItem('auth_user', JSON.stringify(userData));

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.response?.data?.detail || 'فشل تسجيل الدخول'
      };
    }
  };

  // Logout is now an alias for clearAuth
  const logout = clearAuth;

  // Impersonate a church (bishop/super_admin enters as church admin)
  const startImpersonation = (newToken, churchId, churchName) => {
    const storage = getStorage();
    
    // Save original auth
    setOriginalToken(token);
    setOriginalUser(user);
    storage.setItem('original_token', token);
    storage.setItem('original_user', JSON.stringify(user));
    
    // Set impersonation state
    setImpersonating(true);
    setImpersonatedChurch({ id: churchId, name: churchName });
    storage.setItem('impersonating', 'true');
    storage.setItem('impersonated_church', JSON.stringify({ id: churchId, name: churchName }));
    
    // Set new token with church admin permissions
    setToken(newToken);
    storage.setItem('auth_token', newToken);
    
    // Update user role temporarily
    setUser({
      ...user,
      role: 'church_admin',
      church_id: churchId,
      church_name: churchName
    });
  };

  // Exit impersonation and return to bishop view
  const exitImpersonation = () => {
    const storage = getStorage();
    
    if (originalToken && originalUser) {
      setToken(originalToken);
      setUser(originalUser);
      storage.setItem('auth_token', originalToken);
      storage.setItem('auth_user', JSON.stringify(originalUser));
    }
    
    setImpersonating(false);
    setImpersonatedChurch(null);
    setOriginalToken(null);
    setOriginalUser(null);
    
    storage.removeItem('impersonating');
    storage.removeItem('impersonated_church');
    storage.removeItem('original_token');
    storage.removeItem('original_user');
  };

  const isAuthenticated = !!token && !!user;

  const isAdmin = () => {
    return user?.role === 'admin' || user?.role === 'church_admin';
  };

  const isBishop = () => {
    return user?.role === 'bishop' || user?.role === 'super_admin';
  };

  const isSuperAdmin = () => {
    return user?.role === 'super_admin';
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    isAdmin,
    isBishop,
    isSuperAdmin,
    login,
    logout,
    API,
    impersonating,
    impersonatedChurch,
    startImpersonation,
    exitImpersonation
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
